import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../app/appTypes"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../http"

export type StructureDefinitionTypeEnum =
	| "GENERAL"
	| "ORGANIZATIONAL_STRUCTURE"
	| "CUSTOM_DEVICE_STRUCTURE"

export interface StructureDefinitionDto {
	id: string
	name: string
	code: string 
	description: string 
	type: StructureDefinitionTypeEnum
	companyId: number
	validFrom: Date
	validTo: Date | null
	isActive: boolean
}

export interface StructureNodeTreeDto {
	id: string
	sharedId: string
	structureDefinitionId: string
	parentNodeId: string | null
	nodeTypeId: string | null
	name: string
	code: string
	description: string
	nodeTypeName: string
	nodeTypeCode: string
	order: number
	level: number
	path: string | null
	validFrom: Date
	validTo: Date | null
	isActive: boolean
	children: StructureNodeTreeDto[]
}

export interface StructureTreeDto {
	structure: StructureDefinitionDto
	roots: StructureNodeTreeDto[]
}

export interface LoadCompanyStructuresDefinitionsParams {
	onlyActive?: boolean
	forDate?: Date | null
}

export interface LoadStructureTreeParams {
	structureDefinitionId: string
	onlyActive?: boolean
	forDate?: Date | null
}

export interface CreateStructureDefinitionRequest {
	name: string
	code: string
	description: string
	type: StructureDefinitionTypeEnum
	companyId: number
	validFrom: Date
}

export interface UpdateStructureDefinitionRequest {
	id: string
	name: string
	code: string
	description: string
	type: StructureDefinitionTypeEnum
	validFrom: Date
	validTo: Date | null
}

export interface CloseStructureRequest {
	closedAt: Date
}

export interface CreateStructureNodeRequest {
	structureDefinitionId: string
	parentNodeId: string | null
	nodeTypeId: string | null
	name: string
	code: string
	description: string
	order: number
	validFrom: Date
}

export interface UpdateStructureNodeRequest {
	id: string
	parentNodeId: string | null
	nodeTypeId: string | null
	name: string
	code: string
	description: string
	order: number
	validFrom: Date
	validTo: Date | null
}

export const loadCompanyStructuresDefinitions = createAsyncThunk<
	StructureDefinitionDto[],
	LoadCompanyStructuresDefinitionsParams | void,
	AppThunkAPIType
>(
	"structures/load-company-definitions",
	async (args, { dispatch, rejectWithValue }) => {
		const onlyActive = args?.onlyActive ?? false
		const forDate = args?.forDate

		const params = new URLSearchParams()
		params.set("onlyActive", String(onlyActive))
		if (forDate) {
			params.set("forDate", forDate.toISOString())
		}

		const result = await dispatch(getWithAuth({
			url: `api/company-structures/definitions?${params.toString()}`
		}))

		const { payload } = result
		if (getWithAuth.fulfilled.match(result)) {
			return payload as StructureDefinitionDto[]
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)

export const loadOrganizationalStructureDefinition = createAsyncThunk<
	StructureDefinitionDto,
	void,
	AppThunkAPIType
>(
	"structures/load-organizational-definition",
	async (_, { dispatch, rejectWithValue }) => {
		const result = await dispatch(getWithAuth({
			url: "api/company-structures/definitions/organizational-structure",
		}))

		const { payload } = result
		if (getWithAuth.fulfilled.match(result)) {
			return payload as StructureDefinitionDto
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)

export const createStructureDefinition = createAsyncThunk<
	StructureDefinitionDto,
	CreateStructureDefinitionRequest,
	AppThunkAPIType
>(
	"structures/create-definition",
	async (dto, { dispatch, rejectWithValue }) => {
		const result = await dispatch(postWithAuth({
			url: "api/company-structures/definitions",
			payload: dto,
		}))

		const { payload } = result
		if (postWithAuth.fulfilled.match(result)) {
			return payload as StructureDefinitionDto
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)

export const updateStructureDefinition = createAsyncThunk<
	StructureDefinitionDto,
	UpdateStructureDefinitionRequest,
	AppThunkAPIType
>(
	"structures/update-definition",
	async (dto, { dispatch, rejectWithValue }) => {
		const result = await dispatch(putWithAuth({
			url: "api/company-structures/definitions",
			payload: dto,
		}))

		const { payload } = result
		if (putWithAuth.fulfilled.match(result)) {
			return payload as StructureDefinitionDto
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)

export const closeStructureDefinition = createAsyncThunk<
	void,
	{ structureDefinitionId: string; request: CloseStructureRequest },
	AppThunkAPIType
>(
	"structures/close-definition",
	async ({ structureDefinitionId, request }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(postWithAuth({
			url: `api/company-structures/definitions/${structureDefinitionId}/close`,
			payload: request,
		}))

		const { payload } = result
		if (postWithAuth.fulfilled.match(result)) {
			return
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)

export const loadStructureTree = createAsyncThunk<
	StructureTreeDto,
	LoadStructureTreeParams,
	AppThunkAPIType
>(
	"structures/load-tree",
	async ({ structureDefinitionId, onlyActive = false, forDate }, { dispatch, rejectWithValue }) => {
		const params = new URLSearchParams()
		params.set("onlyActive", String(onlyActive))
		if (forDate) {
			params.set("forDate", forDate.toISOString())
		}

		const result = await dispatch(getWithAuth({
			url: `api/company-structures/${structureDefinitionId}/tree?${params.toString()}`,
		}))

		const { payload } = result
		if (getWithAuth.fulfilled.match(result)) {
			return payload as StructureTreeDto
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)

export const createStructureNode = createAsyncThunk<
	StructureNodeTreeDto,
	CreateStructureNodeRequest,
	AppThunkAPIType
>(
	"structures/create-node",
	async (dto, { dispatch, rejectWithValue }) => {
		const result = await dispatch(postWithAuth({
			url: "api/company-structures/nodes",
			payload: dto,
		}))

		const { payload } = result
		if (postWithAuth.fulfilled.match(result)) {
			return payload as StructureNodeTreeDto
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)

export const updateStructureNode = createAsyncThunk<
	StructureNodeTreeDto,
	UpdateStructureNodeRequest,
	AppThunkAPIType
>(
	"structures/update-node",
	async (dto, { dispatch, rejectWithValue }) => {
		const result = await dispatch(putWithAuth({
			url: "api/company-structures/nodes",
			payload: dto,
		}))

		const { payload } = result
		if (putWithAuth.fulfilled.match(result)) {
			return payload as StructureNodeTreeDto
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)

export const deleteStructureNode = createAsyncThunk<
	void,
	{ nodeId: string },
	AppThunkAPIType
>(
	"structures/delete-node",
	async ({ nodeId }, { dispatch, rejectWithValue }) => {
		const result = await dispatch(deleteWithAuth({
			url: `api/company-structures/nodes/${nodeId}`,
		}))

		const { payload } = result
		if (deleteWithAuth.fulfilled.match(result)) {
			return
		}

		return rejectWithValue(payload ?? { kind: "unknown" })
	}
)
